import React from "react";
import { useEffect, useState } from "react";
import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Carousel from "react-material-ui-carousel";
import ContentCards from "../components/homepage/contentCards";
import CustomizedAccordions from "../components/homepage/accordion";
import CustomContainer from "../components/layout/container";
import CustomHeader from "../components/layout/header";

import whyIceberg from "../images/IcebergBlue_Original.jpg";
import whyGreenArrows from "../images/ScenarioArrowsGradient_Green.jpg";
import whyEnerEmis from "../images/Baltic_Wind_SkyOrange_600_200_01a.jpg";


const carouselHeight = "150px";
const Why = () => {
  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { whySubPage } = useParams();

  const router = useNavigate();
  const [pageTitle, setPageTitle] = useState("");
  const [links, setLinks] = useState("");
  const [borderColorToHighlight, setborderColorToHighlight] = useState("");

  const getRouterData = () => {
    if (whySubPage) {
      const url = whySubPage.replace(/-/g, " ");
      setLinks(url);
      const finalString = url.split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      setPageTitle(finalString);
      setTimeout(() => {
        setborderColorToHighlight(theme.palette.primary.dark);
      }, 100);
    }
  };

  const breadcrumbData = [
    {
      name: "Home",
      link: "/",
    },
    {
      name: `Why (${pageTitle})`,
      link: `/why/${whySubPage}`,
    },
  ];

  useEffect(() => {
    getRouterData();
  }, [whySubPage]);

  return (
    <Box>
      <Grid>
        <title>{pageTitle?.toUpperCase()} | MEF</title>
        <meta
          name="description"
          content="Identify & Quantify Macroeconomic/Esg risk"
        />
      </Grid>
      <CustomHeader
        title={`Why?<span style="font-size:1.2rem;color:rgba(84,77,160,1);"> - Global Uncertainty Needs Smart Scenarios</span>`}
        breadcrumbData={breadcrumbData}
      />

      <CustomContainer>
        <Box >
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={4}>
              <ContentCards
                imgSrc={whyIceberg}
                title="Ubiquitous Global Threats & Opportunites"
                objectFit="fill"
                subTitle="Global Economy - Multi-Dimensional Uncertainty"
                borderColor={
                  links == "global risk" ? borderColorToHighlight : "none"
                }
                //example to link in text
                body={`International trade entry/expansion can <b>unlock growth potential</b> & productivity and enable <b>demand/supply diversification</b>.
                But it also brings a higher dimension of competition, contestable markets & risk exposure where <b>uncertainties can be multiplicative</b> & not just free-standing.
                Capturing these effectively requires  a Global, data-driven Economic/Energy/Emissions model that can articulate and quantify
                both <i>magnitude</i> & <i>longevity</i> of Global/National shocks. `}
              />
              <CustomizedAccordions
                title="Global Risk Vectors"
                body={`The types of global forecast risk that MEF (model-based) scenario analysis can help mitigate include: <ul>
                <li>Macroeconomic - Consumer Demand, Fiscal Tax/Spend, Inflation/Commodity Prices, Currency, Trade Flows/Prices</li>
                <li>Supply/Geopolitical- economic/business impacts of supply disruptions </li>
                <li>Energy Use/Emissions - technology breakthroughs and tech-diffusion</li>
                <li>Carbon Transition - Green taxation, technology shocks, Global Carbon budgets</li>
                </ul>`}
              />
              <CustomizedAccordions
                title="How MEF can help ?"
                body={`We assist many different types of firm deal with the anticipation & quantification of Global or Sovereign risks & uncertainties that will impact their domain-level operations across
                Economics, Globalisation/Trade, Finance, Investment and Energy/Emissions.
                One such firm is Decision Analysis Services Ltd which MEF have been working with for almost 25 years to assist them to model/quantify various Global, Economic/Policy scenarios eg around Brexit transition,
                Trump's trade wars - see  <a href='/why/case-study/DAS'> Client Case Study - DAS</a>`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <ContentCards
                imgSrc={whyGreenArrows}
                title="Scenario Analysis - for Strategic Planning"
                subTitle={`Model-based Risk Quantification`}
                borderColor={
                  links == "scenario analysis"
                    ? borderColorToHighlight
                    : "none"
                }
                // <a href='/who/exporters' >
                body={`Using its main, global, macro model <a href='/how/mGEM-global-macro' title="macro Global Econometric Model">mGEM </a> and 
                  its various add-on modules for Finance <a <a href='/how/finGEM-global-finance' title="finance Global Econometric Model"> (finGEM)</a>, 
                  Energy <a href='/how/enGEM-global-energy' title="energy Global Econometric Model"> (enGEM) </a>,  and GHG Emissions <a title="emissions Global Econometric Model"> (eGEM) </a>, MEF can construct tailored and <b>detailed scenarios</b> to analyse "known-unknown" operational business risks.
                Whilst the exact <i>timing</i> of <a title="Nicholas Taleb's notion of unknown-unknowns"> "Black Swan" </a> shocks cannot be predicted in advance, prudential scenario analysis <i> would</i> attempt to regularly quantify the impact of a <b><i>range</i> of contrarian scenarios</b>,
                to boost preparedness across several business risk axes.`}
              />
              <CustomizedAccordions
                title="Stress-Test Official Forecasts"
                body={`Official forecasts from policy authorities and NGOs for the Economy (eg UK OBR, IMF, OECD) and Energy/Emissions (eg IEA, Paris or the US EIA) often involve key forecast assumptions
                for <a title="variables not contained in the forecasting model"> "exogenous" </a> driver variables eg Business or Consumer Confidence. Yet such variables - often the
                <i>key</i> drivers of final forecast trajectories of interest - are often <i>highly</i> uncertain. Its essential therefore to understand the model <i>structure</i> underlying these offiical forecast
                models in order to identify which key assumptions/variables are to be tested in scenario analysis of official forecasts. `}
              />
              <CustomizedAccordions
                title="Client Case Studies - MEF Risk Tools/Apps in Action"
                body={`MEF has provided assistance in identifying and quantifying (global) risk across a range of organisations:
                <ul>
                <li>Exporters/SMEs - <a href='/why/case-study/MissanOil'>Missan Oil Company</a> </li>
                <li>Consultancies - <a href='/why/case-study/DAS'>DAS Consulting Ltd</a> </li>
                <li>Financial Advice -  <a href='/why/case-study/SAI'>SAI Suitable Advice Institute</a> </li>
                <li>Executive Education -   <a href='/why/case-study/AMBSexecEd'>Alliance Manchester Business School(AMBS)</a> </li>
                </ul>
                `}
              // <li>Higher Education -   <a href='/why/case-study/Salford'>Salford University</a> </li>
              // <li>Consultancy -   <a href='/why/case-study/iDEA'>iDEA</a> </li>
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <ContentCards
                imgSrc={whyEnerEmis}
                title="Carbon/Energy Transition Risks"
                subTitle={`Highlighting Risks in Pathways/Policies to 2050`}
                borderColor={
                  links == "transition risks"
                    ? borderColorToHighlight
                    : "none"
                }
                body="<b>Energy</b> has returned as a key risk (and opportunity) in the global economy; the Ukraine conflict has arguably <i>increased</i> the urgency of <b>rapid Carbon transition</b> as Energy moves from
                the Economic/Corporate realm to that of existential National Security. As national Fiscal/Regulatory policy is likely to shift to accelerate Carbon transition, organisations need to
                be aware of their own transformation trajectories - and <b>Esg/Green Taxation exposure</b> - to both 2030 and 2050."
              />
              <CustomizedAccordions
                title="Industrial Level Analysis-Energy/Emissions"
                body="iGEM allows the feed-through of mGEM global/national shocks to 12 industrial sectors (ISIC Rev. 4) across the majority of the 53 economies/zones covered by mGEM.
                In turn, enGEM can translate this industrial mix of output trajectories to build up an accurate projection of associated aggregate national GHG emissions to 2050.
                We can also overlay official Energy use/supply forecasts to 2050 in order to stress-test macroeconomic assumptions underlying these projections.
                Such scenario analysis can then test/explore alternative projections of both global/national/industrial activity and the associated GHG intensity of that output variation."
              />
              <CustomizedAccordions
                title="GreenTech Shocks"
                body={`Technology has always been the wildcard in any medium-term economic projections, moreso now in the "race" to hit challenging (and expanding) national climate
                legislative goals & targets for cleaner industrial/power sectors. MEF's global emissions forecasts are the product of (projections of) industrial sector output and associated
                GHG intensity trends. As the latter are generally judgementally projected trends, they are  perfect candidates to test further in "GreenTech scenario analysis to compliment
                the more traditional macroeconomic sensitivity analysis.`}
              />
            </Grid>
            {/* <Grid item xs={12} md={3}>
              <ContentCards
                imgSrc="/images/HE_Graduation_Blue_Original_600_200_01a.jpg"
                title="TBD"
                subTitle="Real World Tools/Apps"
                borderColor={
                  links == "tbd" ? borderColorToHighlight : "none"
                }
                body="Using risk2NZ App, we impose Official macro/budget projections; clients can scenario-test key assumptions impacting their business areas"
              />
              <CustomizedAccordions
                title="Imparting Applied Skills"
                body="Using mGEM, we impose Official macro/budget projections; clients can scenario-test key assumptions impacting their business areas"
              />
              <CustomizedAccordions
                title="Tailored Classroom Apps"
                body="Using mGEM, we impose Official macro/budget projections; clients can scenario-test key assumptions impacting their business areas"
              />
            </Grid> */}
          </Grid>
        </Box>
      </CustomContainer>
    </Box>
  );
};

export default Why;

// Mobile Apps and Webtools - powered by Global models/data
